import React, { useState, useContext } from "react";
import {
    IconButton,
    Menu,
    MenuItem
} from "@mui/material";
import { ReactComponent as DeleteIcon } from './../../assets/Icons/Delete.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { USER_ROLE } from "types/account";
import { FundAccountMember } from "types/fund-account-member";
import awsExports from "../../aws-exports";
import { CeaseFromFundDialog } from "./cease-from-fund-dialog";
import { useDeleteUserFromFundMutation } from "hooks/use-services/fund-account-invitations.service";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";

interface MyFundsMenuProps {
    role: USER_ROLE;
    fundAccountMembers: FundAccountMember[];
    currentUserSub: string;
    fundAccountId?: number;
    onCease?: () => void;
}

const getCeaseText = (role: USER_ROLE): string => {
    switch (role) {
        case USER_ROLE.ADMIN:
            return "Cease to be an Admin";
        case USER_ROLE.VIEWER:
            return "Cease to be a Viewer";
        case USER_ROLE.ADVISOR:
            return "Cease to be an Advisor";
        default:
            return "Cease to be a Viewer";
    }
};

export const MyFundsMenu: React.FC<
    MyFundsMenuProps
> = ({ role, fundAccountMembers, currentUserSub, fundAccountId, onCease }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const open = Boolean(anchorEl);
    const { mutate: deleteFromFund } = useDeleteUserFromFundMutation();
    const { setRefresh, userData } = useContext<AuthData>(AuthContext);

    // Find current user's fundAccountMemberId
    // First try to find from fundAccountMembers array
    let currentUserMember = fundAccountMembers.find(
        (member) => member.user?.sub === currentUserSub
    );
    
    // If not found in fundAccountMembers, try to find from user's fundAccounts using fundAccountId
    if (!currentUserMember && userData?.user?.fundAccounts && fundAccountId) {
        currentUserMember = userData.user.fundAccounts.find(
            (member) => member.fund?.fundAccountId === fundAccountId
        );
    }
    
    const currentUserMemberId = currentUserMember?.fundAccountMemberId;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (event?: {} | React.MouseEvent | React.KeyboardEvent, reason?: string) => {
        if (event && 'stopPropagation' in event && typeof (event as any).stopPropagation === 'function') {
            (event as React.MouseEvent).stopPropagation();
        }
        setAnchorEl(null);
    };

    const handleCeaseClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setOpenDialog(true);
        handleClose();
    };

    const handleConfirmCease = () => {
        console.log('handleConfirmCease called', { currentUserMemberId, currentUserMember, fundAccountId, fundAccountMembers, userData: userData?.user?.fundAccounts });
        
        if (currentUserMemberId) {
            console.log('Calling deleteFromFund with memberId:', currentUserMemberId);
            deleteFromFund(currentUserMemberId, {
                onSuccess: () => {
                    console.log('Successfully deleted fund account member');
                    setOpenDialog(false);
                    // Refresh user data to update the fund list
                    setRefresh(true);
                    if (onCease) {
                        onCease();
                    }
                },
                onError: (error) => {
                    console.error('Error deleting fund account member:', error);
                },
            });
        } else {
            console.error('currentUserMemberId is undefined. Cannot proceed with deletion.', {
                fundAccountMembers,
                currentUserSub,
                fundAccountId,
                userFundAccounts: userData?.user?.fundAccounts
            });
        }
    };

    // Count admins in the fund
    const adminMembers = fundAccountMembers.filter(
        (member) => member.role === USER_ROLE.ADMIN
    );
    const adminCount = adminMembers.length;

    // Check if current user is super admin
    const isSuperAdmin = currentUserSub === awsExports.neta_super_admin_user_sub;

    // Show menu item based on role:
    // - Show only if there are multiple admins (adminCount > 1) OR if current user is super admin
    const shouldShowCeaseOption = ((adminCount === 1 && currentUserMember?.role !== USER_ROLE.ADMIN) || adminCount > 1) || isSuperAdmin;

    const handleDisabledClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
    };

    return (
        <>
            <CeaseFromFundDialog
                open={openDialog}
                role={role}
                onClose={() => setOpenDialog(false)}
                onConfirm={handleConfirmCease}
            />
            <IconButton
                onClick={shouldShowCeaseOption ? handleClick : handleDisabledClick}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onMouseDown={(e) => e.stopPropagation()}
                >
                <MoreVertIcon sx={{
                    color: !shouldShowCeaseOption ? 'neutral.light' : 'neutral.disabled',
                }} />
            </IconButton>
            {anchorEl && shouldShowCeaseOption && (
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{
                        "& .MuiMenu-paper": { px: 1 },
                    }}
                    MenuListProps={{
                        onClick: (e) => e.stopPropagation(),
                        onMouseDown: (e) => e.stopPropagation(),
                    }}
                >

                    <MenuItem
                        className="contribution-dropdown"
                        onClick={handleCeaseClick}
                    >
                        <DeleteIcon /> {getCeaseText(role)}
                    </MenuItem>
                </Menu>
            )}

        </>
    );
};
